import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import useLocalState from '@hooks/useLocalState'
import useUser from '@context/UserContext/useUser'
// import { useNavigate } from 'react-router-dom'
import useAnalytics from '@hooks/useAnalytics'
import { useQuery } from 'react-query'
import { loadPatient } from '@patient/api/patientProfile'
import { PATIENTS } from '../../../../route.const'
import { PatientModel } from '@context/UserContext/typing'
import { secondToMillis } from '@util/time'
import {
  login as authLogin,
  logout as authLogout,
  validate as authValidation,
  refreshToken,
} from '@patient/api/auth'
import { debug } from '@util/log'
import { LogoutMode } from '@hooks/analytics/typings'
import { mapAuthResponse } from '@patient/context/AuthContext/util'
import dayjs from 'dayjs'
import { BaseAuthContext } from '@common/Login/typing'
import { defaultAuthContext } from '@common/Login/constant'
import useIdle from '@common/Login/useIdle'
import localStore from '@service/local-store'
import useNavigateWithParams from '@hooks/useNavigate'
import { useLocation, useSearchParams } from 'react-router-dom'
export interface Auth {
  userName: string
  email: string
  displayName: string
  issuedAt: string
  expiresAt: string
  access_token: string
  refresh_token: string
  id_token: string
  sub: string
  token_type: string
  token_fetched_at: number
}

interface IAuthContext extends BaseAuthContext<Auth> {}

const authContext: IAuthContext = defaultAuthContext

// eslint-disable-next-line
let intervalId: NodeJS.Timeout | null = null

// The authorization code is single-use, so concurrent/repeated authorize()
// calls must share one /auth/validate request instead of issuing new ones.
let pendingAuthorization: {
  code: string
  request: Promise<{ login: boolean; stopNavigate: boolean }>
} | null = null

const PatientAuthContext = createContext<IAuthContext>(authContext)

export const useAuth = () => useContext(PatientAuthContext)

export const PatientAuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [auth, setAuth] = useLocalState<any>('patient-auth-data')
  const { setUser } = useUser<PatientModel>()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const location = useLocation()
  const navigate = useNavigateWithParams()
  const { trackLogout } = useAnalytics()
  const [error, setError] = useState<string | null>(null)
  const { data, refetch, isError, isLoadingError } = useQuery(
    'patient-details',
    loadPatient,
    {
      enabled: false,
    }
  )

  useEffect(() => {
    if (data) {
      setUser(data, PATIENTS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const isLoggedIn = useMemo(() => {
    if (auth?.token_fetched_at) {
      return (
        auth.token_fetched_at + secondToMillis(auth.expiresAt) >= Date.now()
      )
    } else {
      return false
    }
  }, [auth])

  useEffect(() => {
    if (isLoggedIn) {
      refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  const user = useMemo(() => (auth ? auth : null), [auth])

  const login = async () => {
    const loginEvent = localStore.get('loginEvent')
    if (loginEvent) {
      if (!isLoggedIn && !code) {
        setAuth(null)
        const { data } = await authLogin()
        localStore.set('userType', 'authUser')
        localStore.set('route', location.pathname)
        window.location.replace(data.login)
      }
    } else {
      navigate(`/`)
    }
    localStore.remove('loginEvent')
  }

  const validateCode = async (authCode: string) => {
    const { data } = await authValidation(authCode)
    localStore.remove('route')
    if (data) {
      const dtDate = dayjs(data.birthdate)
      const dtCurrentDate = dayjs(new Date())
      const diffYears = dtCurrentDate.diff(dtDate, 'year', true)

      if (diffYears < 18 || Number.isNaN(diffYears)) {
        window.history.replaceState({}, document.title, '/')
        navigate(`/${PATIENTS}/minor`)
        return {
          login: false,
          stopNavigate: true,
        }
      } else {
        const authData = mapAuthResponse(data)
        setAuth(authData)
        window.history.replaceState({}, document.title, '/')
        return {
          login: true,
          stopNavigate: false,
        }
      }
    } else {
      setError(data.error)
      window.history.replaceState({}, document.title, '/')
      throw new Error(data.error)
    }
  }

  const authorize = async () => {
    if (auth || isLoggedIn || !code) {
      return {
        login: false,
        stopNavigate: true,
      }
    }

    if (pendingAuthorization?.code === code) {
      return pendingAuthorization.request
    }

    const request = validateCode(code)
    pendingAuthorization = { code, request }
    return request
  }

  const logout = async (mode: LogoutMode) => {
    let data = null
    try {
      if (auth) {
        // TODO check if it works on dev stage
        data = await authLogout(auth?.id_token)
        trackLogout(PATIENTS, mode)
      }
    } catch (e) {
      debug('error on logout', e)
    }
    setAuth(null)
    if (intervalId) {
      clearInterval(intervalId)
    }
    if (data?.redirect_uri) {
      // Check if it's an external URL (logout provider URL)
      if (data.redirect_uri.startsWith('http')) {
        // Use window.location for external logout URLs
        window.location.href = data.redirect_uri
      } else {
        // Use React Router navigate for internal URLs
        navigate(data.redirect_uri, { replace: true })
      }
    }
  }

  const triggerRefresh = async (auth: Auth) => {
    debug('Refreshing token')
    const data = await refreshToken(auth.refresh_token)
    if (auth && !data.error) {
      const authData = mapAuthResponse(data)
      const currAuth: Auth = {
        ...authData,
        token_fetched_at: Date.now(),
      }
      setAuth({ ...currAuth })
    }
  }

  useEffect(() => {
    if (auth) {
      const timeInMilliSeconds =
        auth.token_fetched_at + secondToMillis(auth.expiresAt) - Date.now()
      debug(`token expires in :: ${timeInMilliSeconds / 1000}`)
      if (timeInMilliSeconds > 0) {
        if (intervalId) {
          debug('clearing interval for active login')
          clearInterval(intervalId)
        }
        intervalId = setTimeout(() => {
          debug(`setting interval for active login`)
          triggerRefresh(auth)
        }, timeInMilliSeconds)
      }
    }
    return () => {
      if (intervalId) {
        debug('clearing interval for cleanup')
        clearInterval(intervalId)
      }
    }
    //eslint-disable-next-line
  }, [auth])

  const { start, pause } = useIdle(logout)

  useEffect(() => {
    if (auth) {
      debug('starting idle timer')
      start()
    }
    return () => {
      debug('pausing idle timer')
      pause()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])

  useEffect(() => {
    if (isError || isLoadingError) {
      setError('Error loading the Data')
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isLoadingError])

  return (
    <PatientAuthContext.Provider
      value={{
        user,
        isLoggedIn,
        login,
        loginError: error,
        logout,
        authorize,
      }}
    >
      {children}
    </PatientAuthContext.Provider>
  )
}
